import { Schedule, ScheduleInfo } from "@/types/dashboard";
import { formatMinutesToTime } from "./time";

export interface DayIcon {
    name: string;
    active: boolean;
}

export const DAYS_OF_WEEK = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
];

export const calculateNextAction = (schedule: Schedule): string => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinute;
    const currentDayIndex = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    // Convert schedule times to minutes
    const startTime = schedule.start_time;
    const endTime = schedule.end_time;

    // Create DayIcon list
    const dayIconList: DayIcon[] = schedule.week_days.map((active, index) => ({
        name: DAYS_OF_WEEK[index],
        active
    }));

    // If start time equals end time, it means the schedule runs all day
    if (startTime === endTime) {
        return "Will run all day long";
    }

    // Find the next active day
    let nextActiveDay = -1;
    let daysUntilNext = 0;

    // Start checking from the current day
    for (let i = 0; i < 7; i++) {
        const checkDayIndex = (currentDayIndex + i) % 7;
        // Convert Sunday from 0 to 6 to match the schedule array (Monday = 0)
        const scheduleIndex = (checkDayIndex + 6) % 7;

        if (dayIconList[scheduleIndex].active) {
            nextActiveDay = checkDayIndex;
            daysUntilNext = i;
            break;
        }
    }

    if (nextActiveDay === -1) {
        return "No active schedule";
    }

    // If schedule is within the same day
    if (startTime < endTime) {
        if (currentTime < startTime) {
            return daysUntilNext === 0
                ? `Will turn on today at ${formatMinutesToTime(startTime)}`
                : `Will turn on ${dayIconList[(nextActiveDay + 6) % 7].name} at ${formatMinutesToTime(startTime)}`;
        } else if (currentTime >= startTime && currentTime < endTime) {
            return daysUntilNext === 0
                ? `Will turn off today at ${formatMinutesToTime(endTime)}`
                : `Will turn off ${dayIconList[(nextActiveDay + 6) % 7].name} at ${formatMinutesToTime(endTime)}`;
        } else {
            const nextDay = dayIconList[(nextActiveDay + 6) % 7].name;
            return daysUntilNext === 1
                ? `Will turn on tomorrow at ${formatMinutesToTime(startTime)}`
                : `Will turn on ${nextDay} at ${formatMinutesToTime(startTime)}`;
        }
    } else {
        // Schedule crosses midnight
        if (currentTime < endTime) {
            return daysUntilNext === 0
                ? `Will turn off today at ${formatMinutesToTime(endTime)}`
                : `Will turn off ${dayIconList[(nextActiveDay + 6) % 7].name} at ${formatMinutesToTime(endTime)}`;
        } else if (currentTime >= startTime) {
            const nextDay = dayIconList[(nextActiveDay + 6) % 7].name;
            return daysUntilNext === 1
                ? `Will turn off tomorrow at ${formatMinutesToTime(endTime)}`
                : `Will turn off ${nextDay} at ${formatMinutesToTime(endTime)}`;
        } else {
            return daysUntilNext === 0
                ? `Will turn on today at ${formatMinutesToTime(startTime)}`
                : `Will turn on ${dayIconList[(nextActiveDay + 6) % 7].name} at ${formatMinutesToTime(startTime)}`;
        }
    }
};

export const getNextActionText = (scheduleInfo: ScheduleInfo,auxTypeCode:number): string => {
    if (!scheduleInfo?.schedules || scheduleInfo.schedules.length === 0) {
        return "";
    }

    // Filter main pump schedules that are active
    const auxSchedules = scheduleInfo.schedules.filter(
        schedule => schedule.aux_type_code === auxTypeCode && schedule.schedule_state
    );

    if (auxSchedules.length === 0) {
        return "";
    }

    // Get the next action for each schedule
    const nextActions = auxSchedules.map(schedule => ({
        schedule,
        nextAction: calculateNextAction(schedule)
    }));


    // Return the first next action (we could potentially find the earliest one if needed)
    return nextActions[0].nextAction;
};

export const getNextActionTextBySlot = (
  scheduleInfo: ScheduleInfo,
  auxTypeCode: number,
  auxSlotNum: number
): string => {
  if (!scheduleInfo?.schedules || scheduleInfo.schedules.length === 0) {
    return '';
  }

  const auxSchedules = scheduleInfo.schedules.filter(
    (schedule) =>
      schedule.aux_type_code === auxTypeCode &&
      (schedule.aux_slot ?? schedule.slot_num) === auxSlotNum &&
      schedule.schedule_state
  );

  if (auxSchedules.length === 0) {
    return '';
  }

  return calculateNextAction(auxSchedules[0]);
};
